"use client";

interface WorkspacePreviewData {
  invitation_id: string;
  invitation_type: "ONE_TIME" | "UNLIMITED";
  invitation_description: string | null;
  workspace_type: string;
  tutor: {
    id: string;
    name: string;
    first_name: string;
    last_name: string;
    avatar: string | null;
  };
  created_at: string;
}

interface WorkspacePreviewResponse {
  data: WorkspacePreviewData;
}

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Loader2, Users, X } from "lucide-react";

import { toast } from "@/utils/customToast";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS, WORKSPACES_API_ENDPOINTS } from "@/constants/api.ts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import PATHS from "@/routes/paths.ts";

interface JoinResponse {
  data?: {
    workspace_id: string;
  };
  code?: string;
  message?: string;
}

type JoinStatus = "loading" | "preview" | "joining" | "success" | "error" | "not_found";

export default function JoinWorkspace() {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<JoinStatus>("loading");
  const [previewData, setPreviewData] = useState<WorkspacePreviewData | null>(null);

  const getWorkspaceInfo = async () => {
    try {
      setStatus("loading");
      const {
        data: { data },
      } = await axiosApiInstance<WorkspacePreviewResponse>(
        `${WORKSPACES_API_ENDPOINTS(workspaceId ?? "").STUDENT.PREVIEW}`,
      );

      setPreviewData(data);
      setStatus("preview");
    } catch (error: any) {
      console.log(error);
      setStatus("not_found");
    }
  };

  const joinWorkspace = async () => {
    try {
      setStatus("joining");
      const response = await axiosApiInstance.post<JoinResponse>(
        `${API_ENDPOINTS.WORKSPACES.INVITATIONS.INVITATIONS}/${workspaceId}/join`,
      );

      if (response.data?.data?.workspace_id) {
        setStatus("success");
        toast.success(t("workspaces:joinWorkspace.toastSuccess.title"), {
          description: t("workspaces:joinWorkspace.toastSuccess.description"),
        });
        // Delay navigation to show success state
        setTimeout(() => {
          navigate(`${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${response.data.data?.workspace_id}`);
        }, 2000);
      }
    } catch (error: any) {
      setStatus("error");
      toast.error(t("workspaces:joinWorkspace.toastError.title"), {
        description: t("workspaces:joinWorkspace.toastError.description"),
      });
    }
  };

  const handleCancel = () => {
    navigate(PATHS.STUDENT.WORKSPACES.LIST);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const debouncedGetWorkspaceInfo = useDebouncedCallback(getWorkspaceInfo, 300);

  useEffect(() => {
    debouncedGetWorkspaceInfo();
  }, []);

  const getIconAndColor = () => {
    switch (status) {
      case "loading":
        return {
          icon: <Loader2 className="text-muted-foreground h-12 w-12 animate-spin" />,
          gradientFrom: "from-blue-600",
          gradientTo: "to-blue-800",
        };
      case "joining":
        return {
          icon: <Loader2 className="text-muted-foreground h-12 w-12 animate-spin" />,
          gradientFrom: "from-blue-600",
          gradientTo: "to-blue-800",
        };
      case "success":
        return {
          icon: <Check className="h-12 w-12 text-green-600" />,
          gradientFrom: "from-green-600",
          gradientTo: "to-green-800",
        };
      case "error":
        return {
          icon: <X className="h-12 w-12 text-red-600" />,
          gradientFrom: "from-red-600",
          gradientTo: "to-red-800",
        };
      case "not_found":
        return {
          icon: <AlertTriangle className="h-12 w-12 text-red-600" />,
          gradientFrom: "from-red-600",
          gradientTo: "to-red-800",
        };
      default:
        return {
          icon: <Users className="h-12 w-12 text-blue-600" />,
          gradientFrom: "from-blue-600",
          gradientTo: "to-blue-800",
        };
    }
  };

  const { icon, gradientFrom, gradientTo } = getIconAndColor() || {
    icon: <Users className="h-12 w-12 text-blue-600" />,
    gradientFrom: "from-blue-600",
    gradientTo: "to-blue-800",
  };

  // Show workspace preview
  if (status === "preview" && previewData) {
    return (
      <div className="bg-background flex h-dvh max-h-[calc(100dvh_-_4rem)] items-center justify-center p-4">
        <Card className="bg-foreground/50 relative mx-auto w-full max-w-2xl overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
          <div className="absolute inset-0 opacity-20">
            <motion.div
              layout
              className="absolute -top-40 -right-40 size-80 rounded-full bg-gradient-to-br from-blue-600 to-blue-800"
              layoutId="circle1"
              transition={{ layout: { duration: 0.75 } }}
            />
            <motion.div
              layout
              className="absolute -bottom-30 -left-30 size-60 rounded-full bg-gradient-to-tr from-blue-600 to-blue-600"
              layoutId="circle2"
              transition={{ layout: { duration: 0.75 } }}
            />
          </div>

          <CardHeader className="relative z-50 text-center">
            <CardTitle className="text-accent-foreground mb-6 text-2xl">
              {t("workspaces:joinWorkspace.invitationTitle")}
            </CardTitle>
          </CardHeader>

          <CardContent className="relative z-50 space-y-6">
            <div className="space-y-4">
              {previewData.invitation_description && (
                <div className="space-y-2">
                  <span className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.description")}
                  </span>
                  <p className="text-accent-foreground bg-muted/50 rounded-lg p-3 text-sm">
                    {previewData.invitation_description}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.invitationType")}
                  </span>
                  <Badge variant="outline">
                    {t(
                      `workspaces:joinWorkspace.invitationTypes.${previewData.invitation_type}`,
                      previewData.invitation_type,
                    )}
                  </Badge>
                </div>

                <div className="text-muted-foreground text-sm">
                  {t("workspaces:joinWorkspace.invitedBy")}
                </div>

                <div className="relative flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    {/* Backdrop image around avatar */}
                    {previewData.tutor.avatar && (
                      <div className="absolute inset-0 -top-1/2 -left-1/2 flex size-40 items-center justify-center">
                        <img
                          alt=""
                          className="size-32! rounded-full object-cover opacity-30 blur-lg"
                          src={previewData.tutor.avatar}
                        />
                      </div>
                    )}
                    <Avatar className="relative h-20 w-20 border-2 border-white/20 shadow-lg">
                      <AvatarImage
                        alt={previewData.tutor.name}
                        src={previewData.tutor.avatar || undefined}
                      />
                      <AvatarFallback className="text-xl font-bold">
                        {previewData.tutor.first_name.charAt(0).toUpperCase()}
                        {previewData.tutor.last_name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-accent-foreground text-xl font-semibold">
                      {previewData.tutor.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">{t("common:roles.tutor")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button className="flex-1 px-6" variant="outline" onClick={handleCancel}>
                {t("workspaces:joinWorkspace.cancelButton")}
              </Button>
              <Button className="flex-1 px-6" variant={"foreground"} onClick={joinWorkspace}>
                {t("workspaces:joinWorkspace.joinButton")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading/status states
  return (
    <div className="bg-background flex h-dvh max-h-[calc(100dvh_-_4rem)] items-center justify-center p-4">
      <Card className="bg-foreground/50 relative mx-auto w-full max-w-xl overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
        <div className="absolute inset-0 opacity-20">
          <motion.div
            layout
            className={`absolute ${status === "preview" ? "-top-40 -right-40" : "-top-40 -left-40"} size-80 rounded-full bg-gradient-to-br ${gradientFrom} ${gradientTo}`}
            layoutId="circle1"
            transition={{ layout: { duration: 0.75 } }}
          />
          <motion.div
            layout
            className={`absolute ${status === "preview" ? "-bottom-30 -left-30" : "-right-30 -bottom-30"} size-60 rounded-full bg-gradient-to-tr ${gradientFrom} ${gradientTo.replace("800", "600")}`}
            layoutId="circle2"
            transition={{ layout: { duration: 0.75 } }}
          />
        </div>
        <CardContent className="relative z-50 flex flex-col items-center p-12 text-center">
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-primary/20 mb-8 flex h-28 w-28 items-center justify-center rounded-full shadow-xl"
            initial={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            {icon}
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="space-y-4">
              {status === "loading" && (
                <>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("workspaces:joinWorkspace.loadingWorkspace")}
                  </h1>
                  <p className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.pleaseWait")}
                  </p>
                </>
              )}
              {status === "joining" && (
                <>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("workspaces:joinWorkspace.title")}
                  </h1>
                  <p className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.pleaseWait")}
                  </p>
                </>
              )}
              {status === "success" && (
                <>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("workspaces:joinWorkspace.successTitle")}
                  </h1>
                  <p className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.successDescription")}
                  </p>
                </>
              )}
              {status === "error" && (
                <>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("workspaces:joinWorkspace.errorTitle")}
                  </h1>
                  <p className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.toastError.description")}
                  </p>
                </>
              )}
              {status === "not_found" && (
                <>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("workspaces:joinWorkspace.workspaceNotFound")}
                  </h1>
                  <p className="text-muted-foreground">
                    {t("workspaces:joinWorkspace.workspaceNotFoundDescription")}
                  </p>
                </>
              )}
            </div>
          </motion.div>

          <motion.div
            animate={{ opacity: 1 }}
            className="bg-muted mt-8 h-2 w-full max-w-[85%] overflow-hidden rounded-full"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: 0.4 }}
          >
            {(status === "loading" || status === "joining") && (
              <motion.div
                animate={{ width: "100%" }}
                className="bg-accent-foreground/90 h-full"
                initial={{ width: "0%" }}
                transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
              />
            )}
            {status === "success" && (
              <motion.div
                animate={{ width: "100%" }}
                className="h-full bg-green-500"
                initial={{ width: "0%" }}
                transition={{ duration: 0.3 }}
              />
            )}
            {status === "error" && (
              <motion.div
                animate={{ width: "100%" }}
                className="h-full bg-red-500"
                initial={{ width: "0%" }}
                transition={{ duration: 0.3 }}
              />
            )}
            {status === "not_found" && (
              <motion.div
                animate={{ width: "100%" }}
                className="h-full bg-red-500"
                initial={{ width: "0%" }}
                transition={{ duration: 0.3 }}
              />
            )}
          </motion.div>

          {(status === "error" || status === "not_found") && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <Button className="px-8" variant="outline" onClick={handleGoBack}>
                {t("workspaces:joinWorkspace.goBackButton")}
              </Button>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
